import { cva } from 'class-variance-authority';
import { cn } from '../../lib/cn.js';
import { FOCUS_RING } from '../../lib/focus-ring.js';
/**
 * The text link, as classes rather than as a component.
 *
 * Same reasoning as `buttonClasses` above it: a link is an anchor here, a
 * router `Link` there, and a `<button>` in the two places the action does not
 * navigate. A component would have to be told which of the three to render,
 * and the app already knows — what it did not have was one answer to what a
 * link looks like.
 *
 * It did not have one because the treatment was written out by hand ten times,
 * and the ten did not agree: six spellings across the app and the design
 * system, three of them with `hover:text-primary-dark` and a focus ring, six
 * with neither. The six without were all in `apps/web`, so every text link a
 * tutor could reach by keyboard was falling back to whatever outline the
 * browser happened to draw.
 *
 * `underline` is not optional and there is no variant that removes it. A link
 * inside a paragraph that is only a colour is a link only to people who can
 * see the colour, which is WCAG 1.4.1 — and every one of these sits in running
 * text or in a row beside text that is not a link.
 */
export const linkClasses = cva(cn('rounded-sm font-semibold text-primary underline underline-offset-2', 'transition-colors duration-fast hover:text-primary-dark', 'disabled:pointer-events-none disabled:text-text-disabled disabled:no-underline', FOCUS_RING), {
    variants: {
        size: {
            sm: 'text-body-sm',
            md: 'text-body',
            /**
             * Takes the size of the text it sits in. The default, because most of
             * these are a word inside a sentence, and a link that resized the line
             * it is part of would be the odd thing.
             */
            inherit: '',
        },
        /**
         * `standalone` is a link on a line of its own — "Back to students",
         * "Back to the Athena site" — rather than a word in a sentence. Those
         * were 22px tall, a target a thumb misses. This gives them the 44px the
         * touch floor asks for and gives most of it back in negative margin, so
         * the page around them does not move. Inline links keep the line's
         * height; WCAG exempts them, and a taller one would split its line.
         */
        layout: {
            inline: '',
            standalone: 'inline-flex min-h-touch-target-min items-center -my-2.5',
        },
    },
    defaultVariants: { size: 'inherit', layout: 'inline' },
});
//# sourceMappingURL=Link.js.map